import { createSlice } from "@reduxjs/toolkit";

import { addWebSocketCases } from "../helpers/storeAction";
import {
  addNewClosureData,
  deleteClosure,
  fetchClosureListData,
  fetchFilterClosureListData,
  updateClosure,
} from "../thunks/channelClosureThunks";

import type { ChannelClosure } from "../../utils/types/channelClosure";

interface ClosureListState {
  closureList: ChannelClosure[];
  filterClosureList: any[];
  loading: boolean;
  error: string | null;
  isDataFetched?: boolean;
}

const initialState: ClosureListState = {
  closureList: [],
  filterClosureList: [],
  loading: false,
  error: null,
  isDataFetched: false,
};

const closureListSlice = createSlice({
  name: "closureList",
  initialState,
  reducers: {
    resetDataFetched: (state) => {
      state.isDataFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClosureListData.pending, (state) => {
        state.loading = true;
        state.isDataFetched = true;
      })
      .addCase(fetchClosureListData.fulfilled, (state, action) => {
        state.closureList = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchClosureListData.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isDataFetched = false;
        state.loading = false;
      });

    builder
      .addCase(updateClosure.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClosure.fulfilled, (state, action) => {
        state.loading = false;
        state.closureList = state.closureList.map((closure) => {
          if (closure.channelClosureId === action.payload?.channelClosureId) {
            return { ...closure, ...action.payload };
          }
          return closure;
        });
      })
      .addCase(updateClosure.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(deleteClosure.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteClosure.fulfilled, (state, action) => {
        state.loading = false;
        state.closureList = state.closureList.filter(
          (closure) => closure.channelClosureId !== action.payload?.id
        );
      })
      .addCase(deleteClosure.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });

    builder
      .addCase(addNewClosureData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewClosureData.fulfilled, (state, action) => {
        state.loading = false;
        state.closureList = [action.payload, ...state.closureList];
      })
      .addCase(addNewClosureData.rejected, (state) => {
        state.loading = false;
        state.error = "Unknown error… Please try again";
      });
    builder
      .addCase(fetchFilterClosureListData.pending, (state) => {
        state.loading = true;
        state.error = null; // Reset the error
      })
      .addCase(fetchFilterClosureListData.fulfilled, (state, action) => {
        state.loading = false;
        state.filterClosureList = action.payload || []; // Set the fetched data
      })
      .addCase(fetchFilterClosureListData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string; // Set the error message
      });
    addWebSocketCases(builder, "closureList", "closureList");
  },
});
export const { resetDataFetched } = closureListSlice.actions;
export default closureListSlice.reducer;
